'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function FinanceCalculator() {
  const t = useTranslations("tools.financeCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentCalc, setCurrentCalc] = useState('PMT');
  const [showSettings, setShowSettings] = useState(false);
  const [showChart, setShowChart] = useState(false);

  const [inputs, setInputs] = useState({
    N: 10,
    IY: 6,
    PV: 20000,
    PMT: -2000,
    FV: -10000,
    PY: 1,
    CY: 1,
    pmtTiming: 'end'
  });

  const [results, setResults] = useState<any>(null);
  const [chartData, setChartData] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    handleCalculate();
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#10b981',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    background: '#f8fafc',
  };

  const calculateFV = (rate: number, nper: number, pmt: number, pv: number, type: number) => {
    if (rate === 0) {
      return -(pv + pmt * nper);
    }
    const pvif = Math.pow(1 + rate, nper);
    return -(pv * pvif + pmt * (1 + rate * type) * ((pvif - 1) / rate));
  };

  const calculatePMT = (rate: number, nper: number, pv: number, fv: number, type: number) => {
    if (rate === 0) {
      return -(pv + fv) / nper;
    }
    const pvif = Math.pow(1 + rate, nper);
    return -((pv * pvif + fv) * rate) / ((pvif - 1) * (1 + rate * type));
  };

  const calculatePV = (rate: number, nper: number, pmt: number, fv: number, type: number) => {
    if (rate === 0) {
      return -(fv + pmt * nper);
    }
    const pvif = Math.pow(1 + rate, nper);
    return -(fv + pmt * (1 + rate * type) * ((pvif - 1) / rate)) / pvif;
  };

  const calculateNPER = (rate: number, pmt: number, pv: number, fv: number, type: number) => {
    if (rate === 0) {
      return -(pv + fv) / pmt;
    }
    const num = pmt * (1 + rate * type) - fv * rate;
    const den = pv * rate + pmt * (1 + rate * type);
    return Math.log(num / den) / Math.log(1 + rate);
  };

  const calculateRate = (nper: number, pmt: number, pv: number, fv: number, type: number) => {
    let rate = 0.1;

    for (let i = 0; i < 100; i++) {
      let y, f;

      if (rate === 0) {
        y = pv + pmt * nper + fv;
        f = nper * pmt;
      } else {
        const pvif = Math.pow(1 + rate, nper);
        y = pv * pvif + pmt * (1 + rate * type) * ((pvif - 1) / rate) + fv;
        f = pv * nper * pvif / (1 + rate) +
            pmt * (1 + rate * type) * ((nper * pvif / (1 + rate) - (pvif - 1) / (rate * rate)) * rate - (pvif - 1) / rate) / rate;
      }

      if (Math.abs(y) < 0.0001) break;
      rate = rate - y / f;
    }

    return rate;
  };

  const calculateDetails = (rate: number, nper: number, pmt: number, pv: number, fv: number, py: number) => {
    const periods = Math.ceil(nper / py);
    const timeline = [];

    for (let i = 0; i <= periods; i++) {
      const n = i * py;
      let currentPV, currentFV, totalPMT = 0, interest = 0;

      if (rate === 0) {
        currentPV = pv + pmt * n;
        currentFV = -(pv + pmt * n);
        totalPMT = pmt * n;
        interest = 0;
      } else {
        const pvif = Math.pow(1 + rate, n);
        currentPV = pv * pvif + pmt * ((pvif - 1) / rate);
        currentFV = -currentPV;
        totalPMT = pmt * n;
        interest = currentPV - pv - totalPMT;
      }

      timeline.push({
        period: i,
        pv: currentPV,
        fv: currentFV,
        totalPMT: totalPMT,
        interest: interest
      });
    }

    const finalPeriod = timeline[timeline.length - 1];

    return {
      timeline: timeline,
      totalPayments: finalPeriod.totalPMT,
      totalInterest: finalPeriod.interest,
      finalValue: finalPeriod.fv
    };
  };

  const handleCalculate = () => {
    const { N, IY, PV, PMT, FV, PY, CY, pmtTiming } = inputs;
    const i = (IY / 100) / CY;
    const n = N * PY;
    const pmtType = pmtTiming === 'beginning' ? 1 : 0;

    let result;
    let details;

    if (currentCalc === 'FV') {
      result = calculateFV(i, n, PMT, PV, pmtType);
      details = calculateDetails(i, n, PMT, PV, result, PY);
    } else if (currentCalc === 'PMT') {
      result = calculatePMT(i, n, PV, FV, pmtType);
      details = calculateDetails(i, n, result, PV, FV, PY);
    } else if (currentCalc === 'IY') {
      const iResult = calculateRate(n, PMT, PV, FV, pmtType);
      result = iResult * CY * 100;
      details = calculateDetails(iResult, n, PMT, PV, FV, PY);
    } else if (currentCalc === 'N') {
      const nResult = calculateNPER(i, PMT, PV, FV, pmtType);
      result = nResult / PY;
      details = calculateDetails(i, nResult, PMT, PV, FV, PY);
    } else if (currentCalc === 'PV') {
      result = calculatePV(i, n, PMT, FV, pmtType);
      details = calculateDetails(i, n, PMT, result, FV, PY);
    }

    setResults({ value: result, details });
    setChartData(details);
    setShowChart(true);
  };

  const handleClear = () => {
    setInputs({
      N: 10,
      IY: 6,
      PV: 20000,
      PMT: -2000,
      FV: -10000,
      PY: 1,
      CY: 1,
      pmtTiming: 'end'
    });
    setResults(null);
    setShowChart(false);
  };

  const formatCurrency = (value: number) => {
    return '$' + Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const subtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '1000px' },
    { maxWidth: '1200px' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr', gap: '2rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const cardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const tabNavigationStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const },
    { justifyContent: 'center' as const },
    {},
    {}
  );

  const tabBtnStyle = (isActive: boolean) => getResponsiveStyle(
    {
      border: '2px solid ' + (isActive ? 'transparent' : colors.border),
      background: isActive ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
      color: isActive ? 'white' : colors.textSecondary,
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.3s ease'
    },
    { padding: '0.625rem 1rem', fontSize: '0.875rem', flex: '1', minWidth: '60px' },
    { padding: '0.75rem 1.5rem', fontSize: '0.95rem' },
    { padding: '0.75rem 1.5rem', fontSize: '0.95rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    {},
    {}
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', color: colors.textPrimary, fontWeight: 600 },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid ' + colors.border,
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem 1rem' },
    { padding: '0.875rem 1rem' },
    { padding: '0.875rem 1rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1.1rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'white',
      color: colors.textSecondary,
      border: '2px solid ' + colors.border,
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '0.75rem'
    },
    { padding: '0.75rem', fontSize: '0.95rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const h2Style = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700, marginBottom: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultMainStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      marginBottom: '1.5rem'
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultMainValueStyle = getResponsiveStyle(
    { fontWeight: 700 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '2.5rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(37, 99, 235, 0.1)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const h3Style = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 600, marginBottom: '1rem', marginTop: '1.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const listStyle = getResponsiveStyle(
    { paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem',
    lineHeight: 1.6,
    color: colors.textSecondary
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={subtitleStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={{ textAlign: 'center' as const, marginBottom: '2rem' }}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
            <p style={subtitleStyle}>{t("toolSubtitle")}</p>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={cardStyle}>
              <div style={tabNavigationStyle}>
                {['FV', 'PMT', 'IY', 'N', 'PV'].map(calc => (
                  <button
                    key={calc}
                    type="button"
                    style={tabBtnStyle(currentCalc === calc)}
                    onClick={() => setCurrentCalc(calc)}
                  >
                    {calc}
                  </button>
                ))}
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleCalculate(); }}>
                {currentCalc !== 'N' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="input-N">{t("labelN")}</label>
                    <input
                      type="number"
                      id="input-N"
                      style={inputStyle}
                      value={inputs.N}
                      onChange={(e) => setInputs({...inputs, N: parseFloat(e.target.value) || 0})}
                      step="any"
                    />
                  </div>
                )}

                {currentCalc !== 'IY' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="input-IY">{t("labelIY")}</label>
                    <div style={{ position: 'relative' as const }}>
                      <input
                        type="number"
                        id="input-IY"
                        style={{...inputStyle, paddingRight: '2.5rem'}}
                        value={inputs.IY}
                        onChange={(e) => setInputs({...inputs, IY: parseFloat(e.target.value) || 0})}
                        step="any"
                      />
                      <span style={{
                        position: 'absolute' as const,
                        right: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: colors.textSecondary,
                        fontWeight: 600
                      }}>%</span>
                    </div>
                  </div>
                )}

                {currentCalc !== 'PV' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="input-PV">{t("labelPV")}</label>
                    <div style={{ position: 'relative' as const }}>
                      <span style={{
                        position: 'absolute' as const,
                        left: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: colors.textSecondary,
                        fontWeight: 600
                      }}>$</span>
                      <input
                        type="number"
                        id="input-PV"
                        style={{...inputStyle, paddingLeft: '2.5rem'}}
                        value={inputs.PV}
                        onChange={(e) => setInputs({...inputs, PV: parseFloat(e.target.value) || 0})}
                        step="any"
                      />
                    </div>
                  </div>
                )}

                {currentCalc !== 'PMT' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="input-PMT">{t("labelPMT")}</label>
                    <div style={{ position: 'relative' as const }}>
                      <span style={{
                        position: 'absolute' as const,
                        left: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: colors.textSecondary,
                        fontWeight: 600
                      }}>$</span>
                      <input
                        type="number"
                        id="input-PMT"
                        style={{...inputStyle, paddingLeft: '2.5rem'}}
                        value={inputs.PMT}
                        onChange={(e) => setInputs({...inputs, PMT: parseFloat(e.target.value) || 0})}
                        step="any"
                      />
                    </div>
                  </div>
                )}

                {currentCalc !== 'FV' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="input-FV">{t("labelFV")}</label>
                    <div style={{ position: 'relative' as const }}>
                      <span style={{
                        position: 'absolute' as const,
                        left: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: colors.textSecondary,
                        fontWeight: 600
                      }}>$</span>
                      <input
                        type="number"
                        id="input-FV"
                        style={{...inputStyle, paddingLeft: '2.5rem'}}
                        value={inputs.FV}
                        onChange={(e) => setInputs({...inputs, FV: parseFloat(e.target.value) || 0})}
                        step="any"
                      />
                    </div>
                  </div>
                )}

                <div style={{ textAlign: 'center' as const, margin: '1.5rem 0' }}>
                  <button
                    type="button"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.primary,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => setShowSettings(!showSettings)}
                  >
                    ⚙️ {t("settingsButton")}
                  </button>
                </div>

                {showSettings && (
                  <div style={{
                    background: colors.background,
                    padding: '1.5rem',
                    borderRadius: '0.75rem',
                    marginTop: '1rem'
                  }}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="input-PY">{t("labelPY")}</label>
                      <input
                        type="number"
                        id="input-PY"
                        style={inputStyle}
                        value={inputs.PY}
                        onChange={(e) => setInputs({...inputs, PY: parseFloat(e.target.value) || 1})}
                        step="1"
                        min="1"
                      />
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="input-CY">{t("labelCY")}</label>
                      <input
                        type="number"
                        id="input-CY"
                        style={inputStyle}
                        value={inputs.CY}
                        onChange={(e) => setInputs({...inputs, CY: parseFloat(e.target.value) || 1})}
                        step="1"
                        min="1"
                      />
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("labelPmtTiming")}</label>
                      <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: colors.textPrimary }}>
                          <input
                            type="radio"
                            name="pmt-timing"
                            value="beginning"
                            checked={inputs.pmtTiming === 'beginning'}
                            onChange={(e) => setInputs({...inputs, pmtTiming: e.target.value})}
                            style={{ width: 'auto', margin: 0 }}
                          />
                          <span>{t("timingBeginning")}</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: colors.textPrimary }}>
                          <input
                            type="radio"
                            name="pmt-timing"
                            value="end"
                            checked={inputs.pmtTiming === 'end'}
                            onChange={(e) => setInputs({...inputs, pmtTiming: e.target.value})}
                            style={{ width: 'auto', margin: 0 }}
                          />
                          <span>{t("timingEnd")}</span>
                        </label>
                      </div>
                      <small style={{ color: colors.textSecondary, marginTop: '0.5rem', display: 'block' }}>
                        {t("timingNote")}
                      </small>
                    </div>
                  </div>
                )}

                <button type="submit" style={calculateBtnStyle}>
                  {t("calculateButton")}
                </button>
                <button type="button" style={clearBtnStyle} onClick={handleClear}>
                  {t("clearButton")}
                </button>
              </form>
            </div>

            <div style={cardStyle}>
              <h3 style={h2Style}>{t("resultsTitle")}</h3>
              {results && (
                <>
                  <div style={resultMainStyle}>
                    <div style={{ fontSize: '0.95rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                      {currentCalc}
                    </div>
                    <div style={resultMainValueStyle}>
                      {currentCalc === 'IY'
                        ? results.value.toFixed(4) + '%'
                        : currentCalc === 'N'
                        ? results.value.toFixed(2)
                        : formatCurrency(results.value)
                      }
                    </div>
                  </div>

                  {currentCalc !== 'N' && currentCalc !== 'IY' && results.details && (
                    <div style={{
                      background: colors.background,
                      borderRadius: '0.75rem',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '1rem 1.5rem',
                        borderBottom: '1px solid ' + colors.border
                      }}>
                        <span style={{ color: colors.textSecondary, fontWeight: 600 }}>
                          {t("sumOfPayments")}
                        </span>
                        <span style={{ color: colors.textPrimary, fontWeight: 700 }}>
                          {formatCurrency(results.details.totalPayments)}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '1rem 1.5rem'
                      }}>
                        <span style={{ color: colors.textSecondary, fontWeight: 600 }}>
                          {t("totalInterest")}
                        </span>
                        <span style={{ color: colors.textPrimary, fontWeight: 700 }}>
                          {formatCurrency(results.details.totalInterest)}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {showChart && chartData && chartData.timeline && (
            <div style={{
              ...cardStyle,
              marginTop: '2rem'
            }}>
              <div style={{
                textAlign: 'center' as const,
                color: colors.textPrimary,
                fontSize: '1.25rem',
                fontWeight: 700,
                marginBottom: '1.5rem'
              }}>
                {t("chartTitle")}
              </div>

              <svg viewBox="0 0 600 300" style={{ width: '100%', height: 'auto' }}>
                <text x="300" y="20" textAnchor="middle" style={{ fontSize: '16px', fontWeight: 'bold', fill: colors.textPrimary }}>
                  {t("chartTitle")}
                </text>

                {[0, 1, 2, 3, 4, 5].map(i => {
                  const maxValue = Math.max(...chartData.timeline.map((t: any) => Math.max(Math.abs(t.pv), Math.abs(t.fv), Math.abs(t.totalPMT), Math.abs(t.interest))));
                  const minValue = -maxValue;
                  const value = minValue + (maxValue - minValue) * (i / 5);
                  const y = 40 + 220 - ((value - minValue) / (maxValue - minValue)) * 220;

                  return (
                    <g key={i}>
                      <line x1="80" y1={y} x2="560" y2={y} stroke={colors.border} strokeWidth="1" />
                      <text x="70" y={y} textAnchor="end" dominantBaseline="middle" style={{ fontSize: '12px', fill: colors.textSecondary }}>
                        ${(value / 1000).toFixed(0)}K
                      </text>
                    </g>
                  );
                })}

                {Array.from({ length: Math.min(chartData.timeline.length, 11) }).map((_, i) => {
                  const period = Math.floor((chartData.timeline.length - 1) * (i / 10));
                  const x = 80 + (period / (chartData.timeline.length - 1)) * 480;

                  return (
                    <g key={i}>
                      <line x1={x} y1="40" x2={x} y2="260" stroke={colors.border} strokeWidth="1" />
                      <text x={x} y="280" textAnchor="middle" style={{ fontSize: '12px', fill: colors.textSecondary }}>
                        {period}
                      </text>
                    </g>
                  );
                })}

                {[
                  { data: chartData.timeline.map((t: any) => t.pv), color: '#2563eb' },
                  { data: chartData.timeline.map((t: any) => t.fv), color: '#10b981' },
                  { data: chartData.timeline.map((t: any) => t.totalPMT), color: '#f59e0b' },
                  { data: chartData.timeline.map((t: any) => t.interest), color: '#7c3aed' }
                ].map((line, idx) => {
                  const maxValue = Math.max(...chartData.timeline.map((t: any) => Math.max(Math.abs(t.pv), Math.abs(t.fv), Math.abs(t.totalPMT), Math.abs(t.interest))));
                  const minValue = -maxValue;

                  let pathData = '';
                  line.data.forEach((value: number, i: number) => {
                    const x = 80 + (i / (chartData.timeline.length - 1)) * 480;
                    const y = 40 + 220 - ((value - minValue) / (maxValue - minValue)) * 220;
                    pathData += (i === 0 ? 'M' : 'L') + ` ${x} ${y}`;
                  });

                  return <path key={idx} d={pathData} fill="none" stroke={line.color} strokeWidth="2" />;
                })}
              </svg>

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '2rem',
                marginTop: '1.5rem',
                flexWrap: 'wrap' as const
              }}>
                {[
                  { label: 'PV', color: '#2563eb' },
                  { label: 'FV', color: '#10b981' },
                  { label: t("legendSumPMT"), color: '#f59e0b' },
                  { label: t("legendInterest"), color: '#7c3aed' }
                ].map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{
                      width: '1rem',
                      height: '1rem',
                      borderRadius: '0.25rem',
                      background: item.color
                    }} />
                    <span style={{ color: colors.textSecondary, fontSize: '0.9rem' }}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToStep1")}</li>
          <li style={listItemStyle}>{t("howToStep2")}</li>
          <li style={listItemStyle}>{t("howToStep3")}</li>
          <li style={listItemStyle}>{t("howToStep4")}</li>
          <li style={listItemStyle}>{t("howToStep5")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>

        <h3 style={h3Style}>{t("insightsSubtitle1")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("insightsBudget1")}</li>
          <li style={listItemStyle}>{t("insightsBudget2")}</li>
          <li style={listItemStyle}>{t("insightsBudget3")}</li>
        </ul>

        <h3 style={h3Style}>{t("insightsSubtitle2")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("insightsRetirement1")}</li>
          <li style={listItemStyle}>{t("insightsRetirement2")}</li>
          <li style={listItemStyle}>{t("insightsRetirement3")}</li>
        </ul>

        <h3 style={h3Style}>{t("insightsSubtitle3")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("insightsBestPractice1")}</li>
          <li style={listItemStyle}>{t("insightsBestPractice2")}</li>
          <li style={listItemStyle}>{t("insightsBestPractice3")}</li>
          <li style={listItemStyle}>{t("insightsBestPractice4")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={h3Style}>{t("detailsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailsFV")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailsPV")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailsPMT")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle4")}</h3>
        <p style={paragraphStyle}>{t("detailsIY")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle5")}</h3>
        <p style={paragraphStyle}>{t("detailsN")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle6")}</h3>
        <p style={paragraphStyle}>{t("detailsSettings")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <h3 style={h3Style}>{t("faqQuestion1")}</h3>
        <p style={paragraphStyle}>{t("faqAnswer1")}</p>

        <h3 style={h3Style}>{t("faqQuestion2")}</h3>
        <p style={paragraphStyle}>{t("faqAnswer2")}</p>

        <h3 style={h3Style}>{t("faqQuestion3")}</h3>
        <p style={paragraphStyle}>{t("faqAnswer3")}</p>

        <h3 style={h3Style}>{t("faqQuestion4")}</h3>
        <p style={paragraphStyle}>{t("faqAnswer4")}</p>

        <h3 style={h3Style}>{t("faqQuestion5")}</h3>
        <p style={paragraphStyle}>{t("faqAnswer5")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.marshmallowchallenge.com/blog/the-ultimate-budget-calculators-for-2025-stay-organised-stay-stress-free/"
               target="_blank"
               rel="noopener noreferrer"
               style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference1")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.pmcu.org/calculators/"
               target="_blank"
               rel="noopener noreferrer"
               style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference2")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.stocktrak.com/new-stocktrak-calculators-for-2025/"
               target="_blank"
               rel="noopener noreferrer"
               style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference3")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.nasdaq.com/articles/4-lesser-known-benefits-using-online-calculators-financial-planning"
               target="_blank"
               rel="noopener noreferrer"
               style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference4")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.bankrate.com/investing/financial-advisors/best-financial-planning-software/"
               target="_blank"
               rel="noopener noreferrer"
               style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference5")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.whitecoatinvestor.com/best-retirement-calculators-2025/"
               target="_blank"
               rel="noopener noreferrer"
               style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference6")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.nerdwallet.com/finance/calculators"
               target="_blank"
               rel="noopener noreferrer"
               style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference7")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}